import posthog from "posthog-js";
import _throttle from "lodash/throttle";
import { POSTHOG_PARENT_FULL_SNAPSHOT_EVENT } from "../containers/enquiry-centre/enquiry-events.constants";
import { EXTERNAL_APP_URI } from "../common/uri/property-uri.constants";

// FRO-11274: cross-origin iframe replays (Enquiry Centre) go blank after every
// full snapshot our recorder takes â€” rrweb rebuilds the replay DOM from the
// parent snapshot but never re-requests the child's, so the iframe area stays
// empty from that point on (rrweb-io/rrweb#1720). Asking the embedded app to
// re-emit its own full snapshot restores the iframe content.
//
// That child snapshot cannot re-trigger this bridge, so there is no notify
// loop: rrweb's `transformCrossOriginEvent` rewrites an incoming child
// FullSnapshot into an IncrementalSnapshot/Mutation (`isAttachIframe: true`)
// attached to the iframe node before it ever reaches our event stream â€” only
// `EventType.Plugin` is forwarded with its type intact. Verified in the shipped
// posthog-js recorder bundle, not just upstream rrweb.

// rrweb EventType.FullSnapshot
const FULL_SNAPSHOT_EVENT_TYPE = 2;

// Embedded apps that run posthog-js with recordCrossOriginIframes and listen
// for the re-snapshot message. Matched against iframe src so the bridge doesn't
// depend on DOM ids that only exist for it (FRO-11274 review).
const PARTICIPATING_APP_URLS = [EXTERNAL_APP_URI.ENQUIRY_CENTRE_APP];

// A child full snapshot is the most expensive rrweb operation, so bursts (idle
// resume landing on a session rotation) are collapsed to one notify per 2s.
// Keep the trailing edge: the LAST snapshot of a burst is the one whose wipe is
// still on screen, so dropping it would leave the iframe blank until the next
// interval snapshot â€” the very failure this bridge exists to prevent.
const notifyParticipatingIframes = _throttle(() => {
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe: HTMLIFrameElement) => {
        const appUrl = PARTICIPATING_APP_URLS.find(url => iframe.src?.startsWith(url));
        if (appUrl && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: POSTHOG_PARENT_FULL_SNAPSHOT_EVENT }, appUrl);
        }
    });
}, 2000);

// Snapshot batches travel through the normal capture pipeline as `$snapshot`
// events, so the public event stream tells us when a full snapshot happened â€”
// no recorder internals involved. The batch flushes up to ~2s after the
// snapshot was taken, which only stretches the blank-iframe gap the child
// refresh closes by that much.
export function installIframeSnapshotBridge() {
    posthog.on("eventCaptured", event => {
        if (event.event !== "$snapshot") {
            return;
        }
        const snapshotData = event.properties?.$snapshot_data;
        const snapshotEvents = Array.isArray(snapshotData) ? snapshotData : [snapshotData];
        if (snapshotEvents.some(snapshotEvent => snapshotEvent?.type === FULL_SNAPSHOT_EVENT_TYPE)) {
            notifyParticipatingIframes();
        }
    });
}
